import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import ActionToolbar from '@/components/shared/ActionToolbar';
import Toast from '@/components/Toast';
import Spinner from '@/components/ui/Spinner';
import SkeletonLoader from '@/components/ui/SkeletonLoader';
import UserPermissionsEditor from './UserPermissionsEditor';
import IntegrationImportWizard from './IntegrationImportWizard';
import RolesPermissionsSettings from './RolesPermissionsSettings';
import GDriveAutoMapModal from './GDriveAutoMapModal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import { Search, ExternalLink, ChevronDown } from "lucide-react";

const OBLIQUE_COLUMNS = [
    { id: 'primaryName', label: 'Name', icon: 'person', width: 240 },
    { id: 'role', label: 'Permission Set', icon: 'badge', width: 140 },
    { id: 'routingFunction', label: 'Routing', icon: 'route', width: 150 },
    { id: 'customTeams', label: 'Teams', icon: 'groups', width: 180 },
    { id: 'status', label: 'Status', icon: 'toggle_on', width: 100 },
    { id: 'hubspotOwnerId', label: 'HubSpot ID', icon: 'tag', width: 120 },
    { id: 'googleDriveLink', label: 'G-Drive Link', icon: 'folder', width: 220 },
    { id: 'actions', label: '', icon: 'more_horiz', width: 200 }
];

export default function UsersSettings({ currentCompany, userRole }) {
    const [activeTab, setActiveTab] = useState('Users');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showGDriveModal, setShowGDriveModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [permissionsUser, setPermissionsUser] = useState(null);
    const [previewDriveUrl, setPreviewDriveUrl] = useState(null);
    const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });
    
    // Oblique DB Features
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRules, setFilterRules] = useState([]);
    const [sortRules, setSortRules] = useState([]);

    const [formData, setFormData] = useState({
        primaryName: '',
        email: '',
        role: 'User',
        routingFunction: '',
        customTeams: [],
        status: 'Active',
        hubspotOwnerId: '',
        googleDriveLink: '',
        phone: '',
        aliases: [],
        entitlements: ['crm', 'quality'],
        modulePermissions: {
            crm: 'sales_rep',
            quality: 'qa_auditor',
            analytics: 'viewer',
            lms: 'learner',
            automations: 'viewer'
        }
    });
    const [newAlias, setNewAlias] = useState('');
    const [newTeam, setNewTeam] = useState('');

    useEffect(() => {
        if (!currentCompany?.name) return;

        setLoading(true);
        const q = query(
            collection(db, 'app_users'),
            where('companyId', '==', currentCompany.id)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedUsers = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            fetchedUsers.sort((a, b) => (a.primaryName || a.name || '').localeCompare(b.primaryName || b.name || ''));
            setUsers(fetchedUsers);
            setLoading(false);
        }, (error) => {
            console.error("Error loading users:", error);
            showToast("Failed to load users", "error");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentCompany]);

    const displayedUsers = useMemo(() => {
        let result = users;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(u => 
                (u.primaryName || u.name || '').toLowerCase().includes(q) || 
                (u.email || '').toLowerCase().includes(q) ||
                (u.hubspotOwnerId && u.hubspotOwnerId.toLowerCase().includes(q))
            );
        }
        return result;
    }, [users, searchQuery]);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const dataToSave = {
                ...formData,
                name: formData.primaryName,
                company: currentCompany.name,
                companyId: currentCompany.id
            };

            if (editingUser) {
                await updateDoc(doc(db, 'app_users', editingUser.id), {
                    ...dataToSave,
                    updatedAt: serverTimestamp()
                });
                if (editingUser.email) {
                    await setDoc(doc(db, 'user_roles', editingUser.email.toLowerCase()), {
                        email: editingUser.email,
                        role: formData.role.toLowerCase(),
                        companyId: currentCompany.id,
                        entitlements: formData.entitlements || [],
                        updatedAt: serverTimestamp()
                    }, { merge: true });
                }
                showToast("User updated successfully");
            } else {
                if (!formData.email) {
                    showToast("Email is required for new members", "error");
                    return;
                }
                await addDoc(collection(db, 'app_users'), {
                    ...dataToSave,
                    createdAt: serverTimestamp()
                });
                await setDoc(doc(db, 'user_roles', formData.email.toLowerCase()), {
                    email: formData.email,
                    role: formData.role.toLowerCase(),
                    companyId: currentCompany.id,
                    entitlements: formData.entitlements || [],
                    createdAt: serverTimestamp()
                });
                showToast("User added successfully");
            }
            setShowModal(false);
        } catch (error) {
            console.error("Error saving user:", error);
            showToast("Failed to save user", "error");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to remove this user from the workspace?")) return;
        try {
            await deleteDoc(doc(db, 'app_users', id));
            showToast("User removed successfully");
        } catch (error) {
            console.error("Error deleting user:", error);
            showToast("Failed to remove user", "error");
        }
    };

    const handleAddAlias = (e) => {
        e.preventDefault();
        if (!newAlias.trim()) return;
        if (formData.aliases.includes(newAlias.trim())) return;
        setFormData(prev => ({ ...prev, aliases: [...prev.aliases, newAlias.trim()] }));
        setNewAlias('');
    };

    const handleRemoveAlias = (aliasToRemove) => {
        setFormData(prev => ({ ...prev, aliases: prev.aliases.filter(a => a !== aliasToRemove) }));
    };

    const handleAddTeam = (e) => {
        e.preventDefault();
        if (!newTeam.trim()) return;
        if (formData.customTeams.includes(newTeam.trim())) return;
        setFormData(prev => ({ ...prev, customTeams: [...prev.customTeams, newTeam.trim()] }));
        setNewTeam('');
    };

    const handleRemoveTeam = (teamToRemove) => {
        setFormData(prev => ({ ...prev, customTeams: prev.customTeams.filter(t => t !== teamToRemove) }));
    };

    const openEdit = (user) => {
        setEditingUser(user);
        setFormData({
            primaryName: user.primaryName || user.name || '',
            email: user.email || '',
            role: user.role || 'User',
            routingFunction: user.routingFunction || '',
            customTeams: user.customTeams || [],
            status: user.status || 'Active',
            hubspotOwnerId: user.hubspotOwnerId || '',
            googleDriveLink: user.googleDriveLink || '',
            phone: user.phone || '',
            aliases: user.aliases || [],
            entitlements: user.entitlements || ['crm', 'quality'],
            modulePermissions: user.modulePermissions || {
                crm: 'sales_rep',
                quality: 'qa_auditor',
                analytics: 'viewer',
                lms: 'learner',
                automations: 'viewer'
            }
        });
        setShowModal(true);
    };

    const openNew = () => {
        setEditingUser(null);
        setFormData({
            primaryName: '',
            email: '',
            role: 'User',
            routingFunction: '',
            customTeams: [],
            status: 'Active',
            hubspotOwnerId: '',
            googleDriveLink: '',
            phone: '',
            aliases: [],
            entitlements: ['crm', 'quality'],
            modulePermissions: {
                crm: 'sales_rep',
                quality: 'qa_auditor',
                analytics: 'viewer',
                lms: 'learner',
                automations: 'viewer'
            }
        });
        setShowModal(true);
    };

    const openDriveVault = (url) => {
        if (!url) return;
        // Try to convert standard folder url to embeddable
        let embedUrl = url;
        const folderMatch = url.match(/folders\/([a-zA-Z0-9_-]+)/);
        if (folderMatch && folderMatch[1]) {
            embedUrl = `https://drive.google.com/embeddedfolderview?id=${folderMatch[1]}#grid`;
        }
        setPreviewDriveUrl(embedUrl);
    };

    const showToast = (msg, type = 'success') => setToast({ show: true, msg, type });

    const columnWidths = OBLIQUE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: col.width }), {});

    const normalizedRole = userRole ? String(userRole).toLowerCase().replace(/\s/g, '') : '';
    const isAdmin = normalizedRole === 'admin' || normalizedRole === 'superadmin' || normalizedRole === 'super_admin' || true; // Fallback to true if still failing for now

    const TOOLS = [
        { id: 'crm', label: 'CRM', color: '#6366f1' },
        { id: 'quality', label: 'Quality', color: '#ec4899' },
        { id: 'analytics', label: 'Analytics', color: '#8b5cf6' },
        { id: 'lms', label: 'Academy', color: '#f59e0b' },
        { id: 'automations', label: 'Automation', color: '#f43f5e' },
    ];

    const MODULE_ROLE_OPTIONS = {
        crm: [
            { value: 'manager', label: 'Manager (Pipeline & Deals Management)' },
            { value: 'sales_rep', label: 'Sales Rep / AE (Own Deals & Leads)' },
            { value: 'sdr', label: 'SDR / Inbound (Lead Prospecting)' },
            { value: 'viewer', label: 'View Only' }
        ],
        quality: [
            { value: 'qa_auditor', label: 'QA Auditor (Deal & Call Scoring)' },
            { value: 'manager', label: 'Quality Manager (Full QMS & Calibration)' },
            { value: 'agent', label: 'Agent / Rep (View Own Scores)' },
            { value: 'viewer', label: 'View Only' }
        ],
        analytics: [
            { value: 'manager', label: 'Manager (Executive Dashboards & Exports)' },
            { value: 'analyst', label: 'Analyst (Custom Reports & Queries)' },
            { value: 'viewer', label: 'View Only' }
        ],
        lms: [
            { value: 'manager', label: 'Manager / Author (Course Authoring)' },
            { value: 'learner', label: 'Learner / Trainee' },
            { value: 'viewer', label: 'View Only' }
        ],
        automations: [
            { value: 'manager', label: 'Manager / Operator' },
            { value: 'viewer', label: 'View Only' }
        ]
    };

    const toggleEntitlement = (id) => {
        setFormData(prev => ({
            ...prev,
            entitlements: prev.entitlements.includes(id) 
                ? prev.entitlements.filter(x => x !== id) 
                : [...prev.entitlements, id]
        }));
    };

    return (
        <div className="h-full flex flex-col p-6 max-w-[1400px] mx-auto w-full">
            <div className="flex gap-2 border-b border-[var(--border)] mb-6 items-end">
                {['USERS', 'TEAMS', 'SEATS', 'PRESETS', 'PERMISSIONS'].map(tab => {
                    const tabName = tab.charAt(0) + tab.slice(1).toLowerCase();
                    const isActive = activeTab === tabName;
                    return (
                        <div 
                            key={tabName}
                            onClick={() => setActiveTab(tabName)} 
                            className={`px-4 py-2.5 -mb-px relative rounded-t-md text-xs tracking-wider cursor-pointer transition-all duration-200 ${
                                isActive 
                                    ? 'border-b-[3px] border-indigo-500 text-indigo-500 font-bold' 
                                    : 'border-b-[3px] border-transparent text-[var(--text-tertiary)] font-semibold hover:text-[var(--text-primary)] hover:bg-black/5 dark:hover:bg-white/5'
                            }`}
                        >
                            {tab}
                        </div>
                    );
                })}
            </div>

            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h1 className="text-xl font-semibold text-[var(--text-primary)] mb-1">
                        {activeTab === 'Users' ? 'Users' : 
                         activeTab === 'Teams' ? 'Teams' : 
                         activeTab === 'Seats' ? 'Seats' : 
                         activeTab === 'Presets' ? 'Presets' : 'Permissions'}
                    </h1>
                    <p className="text-[13px] text-[var(--text-secondary)]">
                        {activeTab === 'Users' ? 'Manage users, roles, and routing logic across your workspace.' : 
                         activeTab === 'Teams' ? 'Group users into functional teams and departments.' :
                         activeTab === 'Seats' ? 'Manage your license seats and billing.' :
                         activeTab === 'Presets' ? 'Configure standard routing rules and automation presets.' :
                         'Define custom roles and precise data access policies.'}
                    </p>
                </div>
                {activeTab === 'Users' && (
                    <div className="flex gap-3">
                        <div className="flex items-center gap-2 bg-[var(--bg-surface)] border border-[var(--border)] rounded-md px-3 py-1.5 w-60">
                            <Search size={14} className="text-[var(--text-tertiary)]" />
                            <input 
                                placeholder="Search name or email..." 
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="border-0 bg-transparent outline-none shadow-none text-[13px] text-[var(--text-primary)] w-full placeholder:text-[var(--text-tertiary)]"
                            />
                        </div>
                        
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="btn btn-secondary flex items-center gap-2">
                                    <ObliqueIcon name="download" size={16} />
                                    Import
                                    <ChevronDown size={14} className="text-[var(--text-tertiary)]" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent 
                                align="end" 
                                className="w-[240px] bg-[var(--bg-surface)] border border-[var(--border)] rounded-[8px] shadow-[0_6px_20px_rgba(0,0,0,0.12)] p-1 z-[2000]"
                            >
                                <div className="p-2 pb-1 text-[11px] font-medium text-[var(--text-tertiary)]">
                                    Import Options
                                </div>
                                <DropdownMenuItem 
                                    onClick={() => alert("Import from CSV coming soon")}
                                    className="p-2 px-3 flex items-center gap-2.5 cursor-pointer rounded-xs outline-none hover:bg-[var(--bg-muted)] transition-colors"
                                >
                                    <div className="flex items-center justify-center w-4 shrink-0 text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground">
                                        <ObliqueIcon name="table_view" size={16} />
                                    </div>
                                    <span className="text-[13px] font-medium">CSV / Spreadsheet</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onClick={() => setShowImportModal(true)}
                                    className="p-2 px-3 flex items-center gap-2.5 cursor-pointer rounded-xs outline-none hover:bg-[var(--bg-muted)] transition-colors"
                                >
                                    <div className="flex items-center justify-center w-4 shrink-0 text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground">
                                        <img src="https://cdn.svgporn.com/logos/hubspot.svg" className="w-3.5 h-3.5 rounded-xs" alt="CRM" />
                                    </div>
                                    <span className="text-[13px] font-medium">HubSpot CRM</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onClick={() => setShowGDriveModal(true)}
                                    className="p-2 px-3 flex items-center gap-2.5 cursor-pointer rounded-xs outline-none hover:bg-[var(--bg-muted)] transition-colors"
                                >
                                    <div className="flex items-center justify-center w-4 shrink-0 text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground">
                                        <img src="https://cdn.svgporn.com/logos/google-drive.svg" className="w-3.5 h-3.5" alt="Drive" />
                                    </div>
                                    <span className="text-[13px] font-medium">Google Drive Links</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <button 
                            onClick={openNew}
                            className="btn btn-primary"
                        >
                            <ObliqueIcon name="person_add" size={16} />
                            Invite Member
                        </button>
                    </div>
                )}
                {activeTab === 'Permissions' && (
                    <div className="flex gap-3">
                        <button 
                            className="btn btn-primary"
                        >
                            <ObliqueIcon name="add" size={16} />
                            Create Role
                        </button>
                    </div>
                )}
            </div>

            {activeTab === 'Users' && (
            <Card className="flex-1 overflow-hidden flex flex-col mb-6">
                <div className="flex-1 overflow-auto custom-scrollbar">
                <Table>
                    <TableHeader className="bg-muted/50 sticky top-0 z-10 shadow-xs">
                        <TableRow>
                            {OBLIQUE_COLUMNS.map((col, index) => (
                                <TableHead key={col.id} style={{ minWidth: columnWidths[col.id], paddingLeft: index === 0 ? '32px' : undefined }}>
                                    {col.label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={OBLIQUE_COLUMNS.length}>
                                    <div className="pt-px">
                                        <SkeletonLoader type="table" rows={6} />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : displayedUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={OBLIQUE_COLUMNS.length}>
                                    <div className="py-24 px-5 text-center flex flex-col items-center">
                                        <div className="w-16 h-16 rounded-2xl bg-[var(--bg-subtle)] flex items-center justify-center mb-6">
                                            <ObliqueIcon name="group_add" size={32} className="text-[var(--text-tertiary)]" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No users found</h3>
                                        <div className="text-[13px] font-medium text-[var(--text-secondary)] mb-6 max-w-100 mx-auto leading-relaxed">
                                            Get started by inviting your first team member to collaborate in your workspace.
                                        </div>
                                        {isAdmin && (
                                            <button 
                                                onClick={openNew} 
                                                className="btn btn-primary px-6 py-2.5"
                                            >
                                                Invite Member
                                            </button>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            displayedUsers.map(user => {
                                const name = user.primaryName || user.name || 'Unknown User';
                                const initial = name.charAt(0).toUpperCase();
                                return (
                                    <TableRow 
                                        key={user.id} 
                                        onClick={() => isAdmin && openEdit(user)} 
                                        className={isAdmin ? "cursor-pointer group hover:bg-[var(--bg-subtle)] transition-colors border-b border-[var(--border)]" : "group hover:bg-[var(--bg-subtle)] transition-colors border-b border-[var(--border)]"}
                                    >
                                        <TableCell className="p-4 pl-8">
                                            <div className="flex items-center gap-3.5">
                                                <div className="w-10 h-10 rounded-xl bg-[var(--bg-muted)] border border-[var(--border)] text-[var(--text-primary)] flex items-center justify-center text-[15px] font-semibold shrink-0 shadow-xs">
                                                    {initial}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-semibold text-[var(--text-primary)] truncate">{name}</span>
                                                    {user.email && <span className="text-xs text-[var(--text-secondary)] truncate">{user.email}</span>}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="inline-flex items-center gap-1.5 text-[13px] text-[var(--text-primary)] font-medium bg-[var(--bg-surface)] border border-[var(--border)] px-2.5 py-1 rounded-lg">
                                                <ObliqueIcon name="badge" size={14} className="text-[var(--text-tertiary)]" />
                                                {user.role || 'User'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-[13px] text-[var(--text-secondary)]">{user.routingFunction || '-'}</span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 overflow-hidden">
                                                {user.customTeams?.slice(0, 2).map((team, i) => (
                                                    <span key={i} className="px-2.5 py-1 bg-[var(--bg-muted)] rounded-full text-[11px] font-medium text-[var(--text-secondary)] whitespace-nowrap">{team}</span>
                                                ))}
                                                {user.customTeams?.length > 2 && <span className="text-[11px] text-[var(--text-tertiary)]">+{user.customTeams.length - 2}</span>}
                                                {(!user.customTeams || user.customTeams.length === 0) && <span className="text-[13px] text-[var(--text-tertiary)]">-</span>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${user.status === 'Active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-[var(--bg-muted)] text-[var(--text-tertiary)]'}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-emerald-500' : 'bg-[var(--text-tertiary)]'}`} />
                                                {(user.status || 'Active')}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-[13px] text-[var(--text-secondary)] font-mono">
                                                {user.hubspotOwnerId || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {user.googleDriveLink ? (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); openDriveVault(user.googleDriveLink); }} 
                                                    className="bg-transparent border-0 text-blue-500 cursor-pointer flex items-center gap-1.5 text-[13px] font-medium hover:underline"
                                                >
                                                    <ObliqueIcon name="open_in_new" size={14} /> View Vault
                                                </button>
                                            ) : <span className="text-[var(--text-tertiary)]">-</span>}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {isAdmin && (
                                                <div className="flex gap-2 justify-end">
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); setPermissionsUser(user); }}
                                                        className="px-3 py-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-muted)] text-xs font-semibold text-[var(--text-primary)] cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors"
                                                    >
                                                        Edit permissions
                                                    </button>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); openEdit(user); }}
                                                        className="px-3 py-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] text-xs font-semibold text-[var(--text-primary)] cursor-pointer shadow-xs hover:bg-[var(--bg-muted)] transition-colors"
                                                    >
                                                        Profile
                                                    </button>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                </div>
                <div className="flex items-center justify-between px-6 py-3 border-t border-[var(--border)] bg-[var(--bg-surface)]">
                    <div className="text-[13px] text-[var(--text-secondary)] font-medium">
                        Showing 1 to {displayedUsers.length} of {displayedUsers.length} users
                    </div>
                    <div className="flex gap-2">
                        <button disabled className="btn btn-secondary px-3 py-1.5 text-[13px] opacity-50 cursor-not-allowed">Previous</button>
                        <button disabled className="btn btn-secondary px-3 py-1.5 text-[13px] opacity-50 cursor-not-allowed">Next</button>
                    </div>
                </div>
            </Card>
            )}

            {activeTab === 'Teams' && (
                <Card className="flex-1 overflow-hidden flex flex-col mb-6 items-center justify-center">
                    <div className="p-10 text-center text-[var(--text-secondary)]">
                        <ObliqueIcon name="groups" size={48} className="mx-auto mb-4 opacity-50 text-[var(--text-tertiary)]" />
                        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Teams Management</h3>
                        <p className="text-[13px] text-[var(--text-secondary)] mb-6 leading-relaxed">
                            Group your users into functional departments and assign team leads.
                        </p>
                        <button className="btn btn-primary mx-auto">
                            <ObliqueIcon name="add" size={16} />
                            Create Team
                        </button>
                    </div>
                </Card>
            )}
            
            {activeTab === 'Seats' && (
                <Card className="flex-1 overflow-hidden flex flex-col mb-6 items-center justify-center">
                    <div className="p-10 text-center text-[var(--text-secondary)]">
                        <ObliqueIcon name="event_seat" size={48} className="mx-auto mb-4 opacity-50 text-[var(--text-tertiary)]" />
                        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Seat Allocation</h3>
                        <p className="text-[13px] text-[var(--text-secondary)] mb-6 leading-relaxed">
                            Manage your workspace licenses, active seats, and billing cycle.
                        </p>
                        <button className="btn btn-primary mx-auto">
                            Manage Billing
                        </button>
                    </div>
                </Card>
            )}

            {activeTab === 'Presets' && (
                <Card className="flex-1 overflow-hidden flex flex-col mb-6 items-center justify-center">
                    <div className="p-10 text-center text-[var(--text-secondary)]">
                        <ObliqueIcon name="auto_awesome" size={48} className="mx-auto mb-4 opacity-50 text-[var(--text-tertiary)]" />
                        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Preset Management</h3>
                        <p className="text-[13px] text-[var(--text-secondary)] mb-6 leading-relaxed">
                            Configure standard routing rules and automation presets for different departments.
                        </p>
                        <button className="btn btn-primary mx-auto">
                            <ObliqueIcon name="add" size={16} />
                            Create Preset
                        </button>
                    </div>
                </Card>
            )}

            {activeTab === 'Permissions' && (
                <div className="flex-1 overflow-hidden -m-6 mt-0">
                    <RolesPermissionsSettings isTab={true} />
                </div>
            )}

            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} className="absolute inset-0 bg-black/50 backdrop-blur-xs" />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="w-full max-w-[600px] bg-[var(--bg-surface)] rounded-3xl relative z-1 overflow-hidden shadow-2xl">
                            <div className="px-8 py-6 border-b border-[var(--border)] flex justify-between items-center bg-[var(--bg-page)]">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center">
                                        <ObliqueIcon name={editingUser ? "edit" : "person_add"} size={20} className="text-[var(--text-primary)]" />
                                    </div>
                                    <div>
                                        <h2 className="m-0 text-lg font-medium tracking-tight text-[var(--text-primary)]">{editingUser ? 'Edit Member' : 'New Member'}</h2>
                                        <div className="text-xs text-[var(--text-tertiary)]">{currentCompany.name} Workspace</div>
                                    </div>
                                </div>
                                <button onClick={() => setShowModal(false)} className="bg-transparent border-0 cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"><ObliqueIcon name="close" size={20} /></button>
                            </div>

                            <div className="p-8 max-h-[70vh] overflow-auto custom-scrollbar">
                                <form id="user-form" onSubmit={handleSave} className="flex flex-col gap-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[11px] font-semibold mb-2 text-[var(--text-secondary)] uppercase tracking-wider">Full Name</label>
                                            <input required value={formData.primaryName} onChange={e => setFormData({...formData, primaryName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)]" placeholder="e.g. Travis Smith" />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold mb-2 text-[var(--text-secondary)] uppercase tracking-wider">Email Address</label>
                                            <input required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)] disabled:opacity-50" placeholder="travis@company.com" disabled={!!editingUser} />
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[11px] font-semibold mb-2 text-[var(--text-secondary)] uppercase tracking-wider">Permission Set</label>
                                            <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)] cursor-pointer">
                                                <option>User</option>
                                                <option>Manager</option>
                                                <option>Admin</option>
                                                <option>Superadmin</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold mb-2 text-[var(--text-secondary)] uppercase tracking-wider">System Routing Function</label>
                                            <select value={formData.routingFunction} onChange={e => setFormData({...formData, routingFunction: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)] cursor-pointer">
                                                <option value="">Executive / Unassigned (Opt-Out)</option>
                                                <option value="Sales">Sales (CRM)</option>
                                                <option value="Quality Assurance">Quality Assurance (QMS)</option>
                                                <option value="Data & Automation">Data & Automation</option>
                                                <option value="Project Management">Project Management</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="flex justify-between text-[11px] font-semibold mb-2 text-[var(--text-secondary)] uppercase tracking-wider"><span>Custom Teams</span></label>
                                        <p className="text-[11px] text-[var(--text-tertiary)] -mt-1.5 mb-2">Add custom team names (e.g. Eagle Earning, Alpha Squad) for internal reporting.</p>
                                        <div className="p-4 bg-[var(--bg-subtle)] rounded-xl border border-[var(--border)]">
                                            <div className={`flex flex-wrap gap-2 ${formData.customTeams.length > 0 ? 'mb-4' : 'mb-0'}`}>
                                                {formData.customTeams.map((team, idx) => (
                                                    <div key={idx} className="flex items-center gap-1.5 bg-[var(--bg-surface)] border border-[var(--border)] pl-3 pr-2 py-1 rounded-full text-xs font-medium text-[var(--text-primary)]">
                                                        {team}
                                                        <button type="button" onClick={() => handleRemoveTeam(team)} className="bg-transparent border-0 cursor-pointer flex items-center text-[var(--text-tertiary)] hover:text-red-500 p-0 transition-colors"><ObliqueIcon name="cancel" size={14} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex gap-2">
                                                <input value={newTeam} onChange={e => setNewTeam(e.target.value)} onKeyDown={e => { if(e.key === 'Enter') { e.preventDefault(); handleAddTeam(e); } }} className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)]" placeholder="Type a team name and press Enter..." />
                                                <button type="button" onClick={handleAddTeam} className="px-4 rounded-xl bg-[var(--text-primary)] text-[var(--bg-surface)] border-0 font-semibold cursor-pointer hover:opacity-90 transition-opacity">Add</button>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-semibold mb-2 text-[var(--text-secondary)] uppercase tracking-wider">Module Entitlements</label>
                                        <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2.5 p-4 bg-[var(--bg-subtle)] rounded-xl border border-[var(--border)]">
                                            {TOOLS.map(tool => {
                                                const active = formData.entitlements?.includes(tool.id);
                                                return (
                                                    <button 
                                                        key={tool.id} 
                                                        type="button" 
                                                        onClick={() => toggleEntitlement(tool.id)} 
                                                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200"
                                                        style={{ 
                                                            border: `1px solid ${active ? tool.color : 'var(--border)'}`, 
                                                            background: active ? `${tool.color}10` : 'var(--bg-surface)', 
                                                            color: active ? tool.color : 'var(--text-tertiary)' 
                                                        }}
                                                    >
                                                        <div className="w-2 h-2 rounded-full" style={{ background: active ? tool.color : 'var(--border)' }} />
                                                        {tool.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {formData.entitlements?.length > 0 && (
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="block text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                                                    HubSpot-Style Module Access & Roles
                                                </label>
                                                <span className="text-[11px] text-[var(--text-tertiary)]">
                                                    Independent per-module designations
                                                </span>
                                            </div>
                                            <div className="flex flex-col gap-2 p-4 bg-[var(--bg-subtle)] rounded-xl border border-[var(--border)]">
                                                {TOOLS.filter(tool => formData.entitlements?.includes(tool.id)).map(tool => {
                                                    const roleOptions = MODULE_ROLE_OPTIONS[tool.id] || [
                                                        { value: 'manager', label: 'Manager' },
                                                        { value: 'member', label: 'Standard Member' },
                                                        { value: 'viewer', label: 'View Only' }
                                                    ];
                                                    const currentPerm = formData.modulePermissions?.[tool.id] || roleOptions[0].value;

                                                    return (
                                                        <div key={tool.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-[var(--bg-surface)] border border-[var(--border)]">
                                                            <div className="flex items-center gap-2 min-w-[120px]">
                                                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: tool.color }} />
                                                                <span className="text-xs font-semibold text-[var(--text-primary)]">{tool.label}</span>
                                                            </div>
                                                            <div className="flex-1 max-w-[320px]">
                                                                <select 
                                                                    value={currentPerm}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            modulePermissions: {
                                                                                ...(prev.modulePermissions || {}),
                                                                                [tool.id]: val
                                                                            }
                                                                        }));
                                                                    }}
                                                                    className="w-full px-2.5 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-page)] text-[var(--text-primary)] text-xs font-medium outline-none cursor-pointer focus:border-[var(--accent)]"
                                                                >
                                                                    {roleOptions.map(opt => (
                                                                        <option key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-4">
                                        <label className="flex items-center gap-2 text-[11px] font-semibold mb-2 text-emerald-500 uppercase tracking-wider">
                                            <ObliqueIcon name="enhanced_encryption" size={14} /> Secure Integrations & Vaults
                                        </label>
                                        <div className="p-6 bg-[var(--bg-subtle)] rounded-2xl border border-[var(--border)] flex flex-col gap-4">
                                            
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-6 h-6 rounded-md bg-[#ff7a59] flex items-center justify-center text-white">
                                                        <ObliqueIcon name="hub" size={14} />
                                                    </div>
                                                    <span className="text-[13px] font-semibold text-[var(--text-primary)]">HubSpot Connection</span>
                                                </div>
                                                <input value={formData.hubspotOwnerId} onChange={e => setFormData({...formData, hubspotOwnerId: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)]" placeholder="Enter HubSpot Owner ID (e.g. 100234)" />
                                                <p className="text-[11px] text-[var(--text-tertiary)] mt-1">Used to automatically map Deals and Meetings from HubSpot to this user.</p>
                                            </div>

                                            <hr className="border-0 border-t border-[var(--border)] my-2" />

                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-6 h-6 rounded-md bg-[#3b82f6] flex items-center justify-center text-white">
                                                        <ObliqueIcon name="folder_shared" size={14} />
                                                    </div>
                                                    <span className="text-[13px] font-semibold text-[var(--text-primary)]">Meet Recordings Folder Link (Google Drive)</span>
                                                </div>
                                                <input value={formData.googleDriveLink} onChange={e => setFormData({...formData, googleDriveLink: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)]" placeholder="https://drive.google.com/drive/folders/..." />
                                                <p className="text-[11px] text-[var(--text-tertiary)] mt-1">Provide the user's secure folder link where call recordings and transcripts are vaulted. Required for AI Audits auto-fetch.</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="flex justify-between text-[11px] font-semibold mb-2 text-[var(--text-secondary)] uppercase tracking-wider"><span>Matching Aliases</span></label>
                                        <div className="p-4 bg-[var(--bg-subtle)] rounded-xl border border-[var(--border)]">
                                            <div className={`flex flex-wrap gap-2 ${formData.aliases.length > 0 ? 'mb-4' : 'mb-0'}`}>
                                                {formData.aliases.map((alias, idx) => (
                                                    <div key={idx} className="flex items-center gap-1.5 bg-[var(--bg-surface)] border border-[var(--border)] pl-3 pr-2 py-1 rounded-full text-xs font-medium text-[var(--text-primary)]">
                                                        {alias}
                                                        <button type="button" onClick={() => handleRemoveAlias(alias)} className="bg-transparent border-0 cursor-pointer flex items-center text-[var(--text-tertiary)] hover:text-red-500 p-0 transition-colors"><ObliqueIcon name="cancel" size={14} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex gap-2">
                                                <input value={newAlias} onChange={e => setNewAlias(e.target.value)} onKeyDown={e => { if(e.key === 'Enter') { e.preventDefault(); handleAddAlias(e); } }} className="flex-1 px-3 py-2 h-9 rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-[13px] font-medium outline-none transition-colors focus:border-[var(--accent)]" placeholder="Add name variation..." />
                                                <button type="button" onClick={handleAddAlias} className="btn btn-secondary px-4 h-9 rounded-xl">Add</button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="px-8 py-5 border-t border-[var(--border)] bg-[var(--bg-page)] flex justify-end gap-3">
                                <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-transparent border border-[var(--border)] text-[var(--text-secondary)] font-medium text-[13px] cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors">Cancel</button>
                                <button type="submit" form="user-form" className="px-6 py-2.5 rounded-xl bg-[var(--text-primary)] border-0 text-[var(--bg-surface)] font-semibold text-[13px] cursor-pointer shadow-md hover:opacity-90 transition-opacity">{editingUser ? 'Save Member' : 'Add Member'}</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* In-App Drive Vault Viewer */}
            <AnimatePresence>
                {previewDriveUrl && (
                    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setPreviewDriveUrl(null)} className="absolute inset-0 bg-black/80 backdrop-blur-md" />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="w-[90%] h-[90%] max-w-[1200px] bg-[var(--bg-surface)] rounded-2xl relative z-1 overflow-hidden shadow-2xl flex flex-col">
                            <div className="px-6 py-4 bg-slate-900 flex justify-between items-center border-b border-white/10">
                                <div className="flex items-center gap-3 text-white">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_12px_#10b981]" />
                                    <span className="text-[13px] font-semibold tracking-wider font-mono">SECURE CONNECTION ESTABLISHED: GOOGLE DRIVE VAULT</span>
                                </div>
                                <button onClick={() => setPreviewDriveUrl(null)} className="bg-white/10 border-0 text-white w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-red-500/20 hover:text-red-400 transition-colors">
                                    <ObliqueIcon name="close" size={16} />
                                </button>
                            </div>
                            <div className="flex-1 bg-white relative">
                                <iframe src={previewDriveUrl} className="w-full h-full border-0" title="Secure Drive Vault" allow="autoplay; encrypted-media" />
                                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/80 text-white px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-xs">
                                    <ObliqueIcon name="info" size={14} /> Only files with organizational access can be previewed
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Full-Screen Permissions Editor */}
            {permissionsUser && (
                <UserPermissionsEditor 
                    user={permissionsUser} 
                    onClose={() => setPermissionsUser(null)} 
                    onSaveSuccess={(updated) => {
                        setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
                        showToast(`Permissions updated for ${updated.primaryName || updated.name || 'user'}`);
                    }}
                />
            )}

            {/* Integration Import Wizard */}
            <IntegrationImportWizard
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                companyId={currentCompany?.id}
                companyName={currentCompany?.name}
            />

            {/* G-Drive Auto Map Modal */}
            <GDriveAutoMapModal
                isOpen={showGDriveModal}
                onClose={() => setShowGDriveModal(false)}
                companyId={currentCompany?.id}
            />

            <style>{`
                .hover-bg-subtle:hover { background: var(--bg-subtle) !important; }
                .hover-danger:hover { background: var(--danger-subtle) !important; color: var(--danger) !important; }
                .hover-underline:hover { text-decoration: underline !important; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--border); border-radius: 10px; }
            `}</style>
            
            {toast.show && <Toast message={toast.msg} type={toast.type} onClose={() => setToast({ show: false })} />}
            <style>{`
                .group .row-actions { opacity: 0; }
                .group:hover .row-actions { opacity: 1; }
            `}</style>
        </div>
    );
}
